import React from 'react';
import { Joyride } from 'react-joyride';

const steps = [
  {
    target: 'body',
    title: '🎓 Platform Tutorial',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 6 }}>
          This is an interactive simulation of the real experiment interface, using a
          fictitious <strong>Cinema Management System</strong> as the target.
        </p>
        <p style={{ marginBottom: 0 }}>
          The server has been pre-started for this tour. Follow the steps to get
          familiar with the interface before your real experiment begins.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '#sim-timer',
    title: '⏱️ Time Spent',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 6 }}>
          This timer tracks how long you have been actively testing. It starts
          automatically when the server launches.
        </p>
        <p style={{ marginBottom: 0 }}>
          You have a <strong>mandatory testing window</strong>. Once it expires you will
          see a reminder, but you can keep testing until you are ready to move on.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#sim-scope-instructions',
    title: '🎯 Scope & Focus Instructions',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 6 }}>
          This section tells you which features to prioritize during your assessment.
          You can still explore the whole application, but findings within this scope are
          the primary focus.
        </p>
        <p style={{ marginBottom: 0 }}>
          Click the header to expand or collapse it at any time.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#sim-server-info',
    title: '🖥️ Your Target Hostname',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 6 }}>
          Once the server is running, your target's URL appears here. Open it in a new
          tab to access the web application and start pentesting.
        </p>
        <p style={{ marginBottom: 0 }}>
          The timer above tracks your active testing time.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#sim-extra-services',
    title: '🔗 Additional Services',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 0 }}>
          Some targets are provided external services. Their URLs are listed here for
          reference — you <strong style={{ color: '#dc3545' }}>DO NOT</strong> pentest them.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#sim-test-accounts',
    title: '🔑 Test Accounts',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 0 }}>
          Pre-configured credentials are provided so you can log in and explore the
          application without creating your own accounts. Use these during your pentest
          to test different access levels.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#sim-server-controls',
    title: '⚙️ Server Controls',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 6 }}>
          <strong>Restart</strong> — resets the application to a clean state if it
          becomes unresponsive or you need a fresh start.
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>Stop</strong> — shuts the container down. You can restart it again at
          any time within the session.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#sim-project-docs-card',
    title: '📄 Project Documents',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 0 }}>
          These buttons open your assigned website's documentation in a new tab.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#sim-add-finding-btn',
    title: '📋 Finding Form',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 6 }}>
          Click <strong>➕ Add New Finding</strong> to open a finding card. Fill in all
          mandatory fields (marked *) and click <strong>💾 Add</strong> to save.
        </p>
        <p style={{ marginBottom: 0 }}>
          You can add up to 20 findings per website.
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '#sim-bottom-actions',
    title: '💾 Save & Complete',
    content: (
      <div style={{ fontSize: 13 }}>
        <p style={{ marginBottom: 6 }}>
          <strong>Save All Findings</strong> — stores all open findings in one go
          without leaving the page.
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>Complete this Website</strong> — saves your work, stops the server,
          and advances you to the next step of the experiment. Use this when you are
          done testing.
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
  },
];

const joyrideStyles = {
  options: {
    primaryColor: '#0d6efd',
    zIndex: 10000,
    width: 400,
  },
  tooltip: { borderRadius: 8, padding: '16px 20px' },
  tooltipTitle: { fontSize: 15, fontWeight: 700, marginBottom: 8 },
  tooltipContent: { fontSize: 13, padding: 0 },
  buttonNext: { borderRadius: 6, fontSize: 13, padding: '6px 16px' },
  buttonBack: { borderRadius: 6, fontSize: 13, padding: '6px 14px', color: '#0d6efd' },
  buttonSkip: { fontSize: 12, color: '#999' },
};

export default function PlatformTutorialTour({ run, callback }) {
  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      disableOverlayClose
      scrollToFirstStep={false}
      scrollOffset={90}
      spotlightPadding={6}
      styles={joyrideStyles}
      locale={{
        back: '← Back',
        close: 'Close',
        last: 'Finish tour',
        next: 'Next →',
        skip: 'Skip tour',
      }}
      callback={callback}
    />
  );
}
